import adminApiConfig from "../config/adminApiConfig";

const BASE_PATH = "/ai/api/v1/assistant";

// Helper function to find byte sequence in Uint8Array
function findBytes(buffer, sequence, startIndex = 0) {
  for (let i = startIndex; i <= buffer.length - sequence.length; i++) {
    let found = true;
    for (let j = 0; j < sequence.length; j++) {
      if (buffer[i + j] !== sequence[j]) {
        found = false;
        break;
      }
    }
    if (found) {
      return i;
    }
  }
  return -1;
}

const assistantApi = {
  sendMessage: async (
    message,
    username,
    generateAudio = false,
    voice = "en_US-hfc_male-medium"
  ) => {
    try {
      const date = new Date();
      // Use ISO format, often preferred for backend consistency
      const currentDate = date.toString();

      let url = BASE_PATH;
      if (generateAudio) {
        const params = new URLSearchParams({
          generate_audio: true,
          voice: voice,
        });
        url = `${BASE_PATH}?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Sending JSON request
          ...adminApiConfig.headers,
          // Ensure Accept header hints we can handle multipart if needed,
          // though the server decides based on the generate_audio param.
          // 'Accept': 'application/json, multipart/form-data'
        },
        credentials: "include",
        body: JSON.stringify({
          messages: [{ role: "user", content: message }],
          username,
          current_date: currentDate, // Sending ISO string
        }),
      });

      if (!response.ok) {
        let errorDetail = `Request failed with status ${response.status}`;
        try {
          // Try to parse error JSON, but handle cases where it's not JSON
          const errorJson = await response.json();
          errorDetail = errorJson.detail || JSON.stringify(errorJson);
        } catch (parseError) {
          // If parsing fails, try to get text
          try {
            errorDetail = await response.text();
          } catch (textError) {
            // Fallback if even text fails
            console.error("Could not parse error response body");
          }
        }
        throw new Error(errorDetail);
      }

      const contentType = response.headers.get("Content-Type") || "";

      // Check if the *response* is multipart
      if (generateAudio && contentType.includes("multipart/form-data")) {
        // console.log("Received multipart response");
        const responseData = await response.arrayBuffer();

        // *** FIX: Call using object name ***
        const result = await assistantApi._parseMultipartResponse(
          responseData,
          contentType
        );
        // *** End Fix ***

        if (result.audio) {
          //   console.log("Audio data found, attempting to play...");
          const audioBlob = new Blob([result.audio], { type: "audio/wav" });
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          audio
            .play()
            .catch((err) => console.error("Error playing audio:", err));
          // Optional: Revoke URL after some time or when done
          // setTimeout(() => URL.revokeObjectURL(audioUrl), 60000); // Clean up after 1 minute
        } else {
          console.warn(
            "Multipart response received but no audio data found in parser result."
          );
        }

        if (!result.json) {
          console.error(
            "Multipart response received but no JSON data found in parser result."
          );
          throw new Error("Failed to parse JSON data from multipart response.");
        }

        return result.json; // Return only the JSON part
      } else {
        // Handle regular JSON response
        console.log("Received JSON response");
        return await response.json();
      }
    } catch (error) {
      // Log the full error object for more details if needed
      console.error("Error sending message:", error);
      // Re-throw the original error or a new one with a consistent message
      throw new Error(`Failed to communicate with assistant: ${error.message}`);
    }
  },

  // *** REVISED PARSER ***
  _parseMultipartResponse: async (arrayBuffer, contentTypeHeader) => {
    const result = {
      json: null,
      audio: null,
    };

    const boundaryMatch = contentTypeHeader.match(/boundary=([^;]+)/i);
    if (!boundaryMatch) {
      throw new Error("Could not find boundary in content type");
    }
    // The actual boundary string in the body starts with "--"
    const boundary = `--${boundaryMatch[1]}`;
    // console.log("Using boundary:", boundary);

    const buffer = new Uint8Array(arrayBuffer);
    const decoder = new TextDecoder("utf-8");
    const boundaryBytes = new TextEncoder().encode(boundary);
    const rnBytes = new TextEncoder().encode("\r\n");
    const headerSeparatorBytes = new TextEncoder().encode("\r\n\r\n");

    let currentPos = 0;
    let partIndex = 0;

    while (currentPos < buffer.length) {
      const boundaryStart = findBytes(buffer, boundaryBytes, currentPos);
      if (boundaryStart === -1) {
        console.warn("Could not find next boundary marker. Pos:", currentPos);
        break; // No more boundaries
      }
      //   console.log(`Found boundary ${partIndex} at index: ${boundaryStart}`);

      // Check for the final boundary marker "--"
      if (
        buffer[boundaryStart + boundaryBytes.length] === 45 &&
        buffer[boundaryStart + boundaryBytes.length + 1] === 45
      ) {
        // console.log("Found final boundary.");
        break; // End of multipart message
      }

      // Find the start of the headers for this part
      const headerStart = boundaryStart + boundaryBytes.length;
      // Headers end with \r\n\r\n
      const headerEnd = findBytes(buffer, headerSeparatorBytes, headerStart);
      if (headerEnd === -1) {
        console.warn(
          `Could not find header separator for part ${partIndex}. HeaderStart: ${headerStart}`
        );
        currentPos = boundaryStart + boundaryBytes.length; // Skip boundary and try again
        continue; // Malformed part
      }
      //   console.log(
      //     `Found header end for part ${partIndex} at index: ${headerEnd}`
      //   );

      // Decode the headers section
      const headersBytes = buffer.slice(headerStart, headerEnd);
      const headersText = decoder.decode(headersBytes).trim();
      //   console.log(`Part ${partIndex} Headers:\n${headersText}`);

      // Find the start of the next boundary to determine the end of the current part's body
      const nextBoundaryStart = findBytes(
        buffer,
        boundaryBytes,
        headerEnd + headerSeparatorBytes.length
      );
      const bodyEnd =
        nextBoundaryStart !== -1
          ? nextBoundaryStart - rnBytes.length
          : buffer.length; // Body ends right before the \r\n preceding the next boundary
      if (nextBoundaryStart === -1) {
        console.warn(
          "Could not find next boundary, assuming end of buffer for body end."
        );
      }
      //   console.log(
      //     `Body for part ${partIndex} is from ${
      //       headerEnd + headerSeparatorBytes.length
      //     } to ${bodyEnd}`
      //   );

      // Extract the body bytes
      const bodyBytes = buffer.slice(
        headerEnd + headerSeparatorBytes.length,
        bodyEnd
      );

      // Process based on headers
      if (headersText.includes("Content-Type: application/json")) {
        // console.log(`Parsing part ${partIndex} as JSON`);
        try {
          const jsonText = decoder.decode(bodyBytes);
          //   console.log("Raw JSON text:", jsonText);
          result.json = JSON.parse(jsonText);
          //   console.log("Parsed JSON:", result.json);
        } catch (e) {
          console.error(
            `Error parsing JSON part ${partIndex}:`,
            e,
            "Raw text:",
            decoder.decode(bodyBytes)
          );
        }
      } else if (headersText.includes("Content-Type: audio/wav")) {
        // console.log(
        //   `Found audio/wav part ${partIndex}, size: ${bodyBytes.length} bytes`
        // );
        result.audio = bodyBytes.buffer.slice(
          bodyBytes.byteOffset,
          bodyBytes.byteOffset + bodyBytes.byteLength
        ); // Get ArrayBuffer slice
      } else {
        console.log(`Ignoring part ${partIndex} with headers: ${headersText}`);
      }

      // Move position to the start of the next boundary for the next iteration
      currentPos = headerEnd + headerSeparatorBytes.length; // Start searching after this part's headers
      if (nextBoundaryStart !== -1) {
        currentPos = nextBoundaryStart; // Jump to the start of the next boundary found
      } else {
        break; // Should not happen if final boundary was handled, but safe exit
      }
      partIndex++;
    }
    // console.log("Finished parsing multipart response.");
    return result;
  },
};

export default assistantApi;
